import * as Sentry from "@sentry/react";
import { useState, useEffect, useMemo, useRef } from "react";

import { revokeToken } from "../http";
import { config } from "../config/app";
import type { User } from "@entities/user";
import type { Company } from "@entities/company";
import { UserFaultException } from "@shared/exceptions";
import { clearOrderDraft } from "@features/order/create";
import { checkSession } from "@features/auth/check-session";
import { discardCompanyQueries } from "@shared/react-query";
import { AuthenticationContext } from "./authentication-context";
import { RoleAbility, AccessControl as AC } from "../access-control";
import { LoadingStateScreen } from "@shared/ui/loading-state-screen";
import {
  findCompanyByIdOrFirst,
  forgetActiveCompany,
  rememberActiveCompany,
  shouldLetUserIn,
} from "./helpers";

const getActiveCompanyId = () =>
  localStorage.getItem(config.LS.ACTIVE_COMPANY_ID);

export const AuthenticationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isSessionChecked, setSessionChecked] = useState(false);
  const [invalidationHandle, setInvalidationHandle] = useState<number>(0);
  const [activeCompany, setActiveCompany] = useState<Company | void>(void 0);
  const [isCompaniesLoaded, setIsCompaniesLoaded] = useState<boolean>(false);

  /**
   * A superuser acting on behalf of a company is its manager: everything
   * written for a manager applies to them as is.
   */
  const isCurrentCompanyManager = useMemo<boolean>(
    () =>
      !!activeCompany &&
      (!!user?.isSuperuser ||
        !!user?.userCompanies?.find(
          (c) => c.isManager && c.companyId === activeCompany.id,
        )),
    [activeCompany, user, user?.userCompanies],
  );

  const isSuperuserActingBehalfOfCompany = useMemo(
    () => !!(user?.isSuperuser && activeCompany),
    [activeCompany, user],
  );

  const isSuperuserInAdminMode = !!user?.isSuperuser && !activeCompany;

  const accountTitle = useMemo(() => {
    if (!activeCompany) return "Not a company member";

    if (!user?.id) return "";
    if (user.isSuperuser) return "Superuser";
    if (isCurrentCompanyManager) return `Manager at ${activeCompany?.name}`;

    return `Staff member at ${activeCompany?.name}`;
  }, [activeCompany, user, isCurrentCompanyManager]);

  const isManagerInInternalCompany = (companyId: number) =>
    (isSuperuserActingBehalfOfCompany &&
      activeCompany?.serviceId === companyId) ||
    !!user?.userCompanies?.find(
      // @ts-ignore
      (c) => c.isManager && c.company.serviceId === companyId,
    );

  useEffect(() => {
    Sentry.setTag("company", void 0);
    Sentry.setTag(
      "company",
      activeCompany
        ? `${activeCompany.name} (ID: ${activeCompany.id})`
        : "No company",
    );
  }, [activeCompany]);

  /**
   * Discards the previous company's cached data whenever the active company
   * changes, whichever code path changed it. Resolving the company on load has
   * no previous company, so that run discards nothing.
   */
  const previousCompanyId = useRef(activeCompany?.id);

  useEffect(() => {
    const previous = previousCompanyId.current;

    if (previous === activeCompany?.id) return;

    previousCompanyId.current = activeCompany?.id;

    if (previous !== undefined) discardCompanyQueries(previous);
  }, [activeCompany?.id]);

  useEffect(() => {
    Sentry.setTag("user", void 0);
    Sentry.setTag(
      "user",
      user ? `${user?.firstName} ${user?.lastName} (ID: ${user?.id})` : "Guest",
    );
  }, [user]);

  useEffect(() => {
    checkSession()
      .then((user) => {
        if (!user?.id) {
          throw new UserFaultException("Unauthorized");
        }

        setUser(user);

        const cachedActiveCompanyId = user.isSuperuser
          ? null
          : getActiveCompanyId();

        const activeCompany = !user?.isSuperuser
          ? findCompanyByIdOrFirst(
              (user?.companies as Company[]) || [],
              Number(cachedActiveCompanyId),
            )
          : null;

        if (activeCompany) {
          setActiveCompany(activeCompany as Company);
          rememberActiveCompany(activeCompany as Company);
        }

        /**
         * A superuser always starts outside on-behalf mode, so a company header
         * left over from before the reload must go — otherwise requests stay
         * scoped to a company the UI no longer shows as active. Skipped when
         * the session is merely revalidated while acting on behalf.
         */
        if (user.isSuperuser && previousCompanyId.current === undefined) {
          forgetActiveCompany();
        }
      })
      /**
       * The remembered company is deliberately kept: it is a UI preference, not
       * a credential, and it is meaningless without a valid token. Keeping it
       * means an expired session returns the user to the company they were
       * working in — and lets a draft scoped to that company be restored.
       * A membership that has since been revoked falls back to the first
       * company via `findCompanyByIdOrFirst`.
       */
      .catch(() => {
        setUser({} as User);
      })
      .finally(() => {
        setSessionChecked(true);
        setIsCompaniesLoaded(true);
      });
  }, [invalidationHandle]);

  const abilities = useMemo<RoleAbility>(() => {
    switch (true) {
      case isCurrentCompanyManager:
        return AC.abilities.manager;
      case !!activeCompany:
        return AC.abilities.staffMember;
      default:
        return AC.abilities.unassigned;
    }
  }, [isCurrentCompanyManager, activeCompany]);

  if (
    !shouldLetUserIn({
      isSuperUser: user?.isSuperuser || false,
      companies: user?.companies || [],
      pathname: window.location.pathname,
      isCompaniesLoaded,
      isSessionChecked,
      activeCompany,
    })
  ) {
    return <LoadingStateScreen w="full" h="95vh" />;
  }

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        abilities,
        isCurrentCompanyManager,
        loggedIn: !!user?.id,
        isSuperuserActingBehalfOfCompany,
        isSuperuserInAdminMode,
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        email: user?.email ?? "",
        accountTitle: accountTitle ?? "",
        isManagerInInternalCompany,
        activeCompany,

        setUser,

        logOut: (redirectTo?: string) => {
          revokeToken();
          setUser({} as User);
          forgetActiveCompany();
          clearOrderDraft();
          window.location.href =
            typeof redirectTo === "string" ? redirectTo : "/login";
        },

        setActiveCompany,
        revalidateSession: () => setInvalidationHandle((prev) => prev + 1),
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
